if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery');
        const mediaGalleryDestination = html.querySelector(`media-gallery`);
        
        if (!mediaGallerySource || !mediaGalleryDestination) return;

        // Update main gallery (viewer)
        const mainGallerySource = mediaGallerySource.querySelector('ul[id^="Slider-Gallery"]');
        const mainGalleryDestination = mediaGalleryDestination.querySelector('ul[id^="Slider-Gallery"]');

        const refreshSourceData = (sourceElement) => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const sourceItems = Array.from(sourceElement.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(sourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            sourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [sourceItems, sourceSet, sourceMap];
        };

        if (mainGallerySource && mainGalleryDestination) {
          const featuredMediaId = `${this.dataset.section}-${variantFeaturedMediaId}`;
          
          // Get all destination items (images for the new variant)
          const mediaGalleryDestinationItems = Array.from(
            mainGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          
          // Get current source items
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData(mainGallerySource);
          
          // Step 1: Remove items that are not in the new variant
          for (let i = mediaGallerySourceItems.length - 1; i >= 0; i--) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
            }
          }
          
          // Step 2: Clear all active states
          mainGallerySource.querySelectorAll('li[data-media-id]').forEach((item) => {
            item.classList.remove('is-active');
          });
          
          // Step 3: Build a map of all current source items (before any changes)
          const allSourceItemsBefore = Array.from(mainGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceItemsMap = new Map();
          allSourceItemsBefore.forEach(item => {
            sourceItemsMap.set(item.dataset.mediaId, item);
          });
          
          // Step 4: Remove all items from DOM temporarily
          allSourceItemsBefore.forEach(item => {
            item.remove();
          });
          
          // Step 5: Rebuild gallery in the exact order from destination
          mediaGalleryDestinationItems.forEach(destinationItem => {
            const mediaId = destinationItem.dataset.mediaId;
            let sourceItem = sourceItemsMap.get(mediaId);
            
            if (!sourceItem) {
              // Clone new item if it doesn't exist in source
              sourceItem = destinationItem.cloneNode(true);
            }
            
            // Add to gallery in the correct order
            mainGallerySource.appendChild(sourceItem);
          });
          
          // Refresh source data after rebuild
          [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData(mainGallerySource);
        }

        // Update thumbnails gallery - replace completely to ensure correct variant images
        const thumbnailsSource = mediaGallerySource.querySelector('ul[id^="Slider-Thumbnails"]');
        const thumbnailsDestination = mediaGalleryDestination.querySelector('ul[id^="Slider-Thumbnails"]');

        if (thumbnailsSource && thumbnailsDestination) {
          // Get all thumbnail items from destination (new variant)
          const thumbnailDestinationItems = Array.from(
            thumbnailsDestination.querySelectorAll('li[data-target]')
          );
          
          // Get the featured media thumbnail (first one) separately
          const featuredThumbnail = thumbnailsDestination.querySelector('li[id^="Slide-Thumbnails"]:first-child');
          
          // Clear existing thumbnails
          thumbnailsSource.innerHTML = '';
          
          // Add featured thumbnail first if it exists
          if (featuredThumbnail) {
            thumbnailsSource.appendChild(featuredThumbnail.cloneNode(true));
          }
          
          // Add all other thumbnails
          thumbnailDestinationItems.forEach((item) => {
            // Skip if it's the featured media (already added)
            if (item.id !== featuredThumbnail?.id) {
              thumbnailsSource.appendChild(item.cloneNode(true));
            }
          });
          
          // Re-initialize thumbnail click handlers
          const newThumbnails = thumbnailsSource.querySelectorAll('[data-target]');
          newThumbnails.forEach((thumbnail) => {
            const button = thumbnail.querySelector('button');
            if (button && !button.hasAttribute('data-listener-added')) {
              button.setAttribute('data-listener-added', 'true');
              const mediaId = thumbnail.dataset.target;
              button.addEventListener('click', () => {
                mediaGallerySource.setActiveMedia(mediaId, false);
              });
            }
          });
        }

        // Wait for DOM updates to complete, then set featured media as active
        // Use multiple animation frames to ensure DOM is fully updated and slider is ready
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const featuredMediaId = `${this.dataset.section}-${variantFeaturedMediaId}`;
            const galleryList = mediaGallerySource?.querySelector('ul[id^="Slider-Gallery"]');
            const featuredMediaElement = galleryList?.querySelector(`li[data-media-id="${featuredMediaId}"]`);
            
            if (featuredMediaElement && mediaGallerySource?.setActiveMedia) {
              // Reset slider pages if slider exists
              const viewer = mediaGallerySource.querySelector('[id^="GalleryViewer"]');
              if (viewer?.slider) {
                viewer.slider.resetPages();
              }
              
              // Set the featured media as active
              // The setActiveMedia function will handle positioning and activation
              mediaGallerySource.setActiveMedia(featuredMediaId, true);
            } else if (!featuredMediaElement) {
              // If featured media element is not found, log for debugging
              console.warn(`Featured media element not found for variant: ${variantFeaturedMediaId}`);
              console.log('Available media IDs:', Array.from(galleryList?.querySelectorAll('li[data-media-id]') || []).map(el => el.dataset.mediaId));
            }
          });
        });

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
